import { useState } from "react";
import type { FormEvent, ReactNode } from "react";
import { Link } from "react-router-dom";
import { useMutation } from "@tanstack/react-query";
import { ShieldCheck, Check, Info } from "../components/icons.js";
import { api, ApiError } from "../lib/api.js";
import { AuthBrand } from "./Login.js";

/**
 * Forgot password — /forgot-password (public).
 * POSTs the email to /auth/password/forgot, which is NON-ENUMERATING by design: it always
 * answers 200 {ok:true} whether or not the address has an account. So this screen shows the
 * SAME neutral confirmation for every submitted address — it can never reveal whether a given
 * email is registered. Only a transport/5xx fault (which says nothing about any account) is
 * surfaced as an error.
 */

/** The one outcome message — identical regardless of the server's real answer. */
const NEUTRAL_CONFIRMATION =
  "If an account exists for that email, we've sent a link to reset your password. Check your inbox (and spam).";

export function ForgotPassword(): ReactNode {
  const [email, setEmail] = useState("");

  const forgot = useMutation({
    mutationFn: (e: string) => api.post<{ ok: boolean }>("/auth/password/forgot", { email: e }),
  });

  // Match or no-match, throttled or not — all collapse to the same confirmation. Only a
  // transport error or 5xx (never account-revealing) is worth showing as a failure.
  const hardFail = forgot.isError && (!(forgot.error instanceof ApiError) || forgot.error.status >= 500);
  const sent = (forgot.isSuccess || forgot.isError) && !hardFail;

  const onSubmit = (e: FormEvent): void => {
    e.preventDefault();
    if (!email.includes("@") || forgot.isPending) return;
    forgot.mutate(email.trim());
  };

  return (
    <div className="auth-wrap t1-auth">
        <AuthBrand />
      <div className="auth-card">
        <div className="auth-head">
          <span className="mark">
            <ShieldCheck />
          </span>
          <h1>Reset your password</h1>
          {!sent && <p>Enter your email and we'll send you a link to set a new password.</p>}
        </div>

        {sent ? (
          <>
            <div className="banner info" style={{ textAlign: "left", marginBottom: 16 }}>
              <Check />
              <div>
                {NEUTRAL_CONFIRMATION}
                <div className="sm" style={{ marginTop: 6, opacity: 0.85 }}>
                  The reset link expires in 1 hour. If you don't see it, check your spam folder.
                </div>
              </div>
            </div>
            <Link to="/login" className="btn primary" style={{ width: "100%", justifyContent: "center" }}>
              Back to sign in
            </Link>
          </>
        ) : (
          <form onSubmit={onSubmit}>
            <div className="field">
              <label htmlFor="forgot-email">Email</label>
              <input
                id="forgot-email"
                className="input"
                type="email"
                autoComplete="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@company.com"
              />
            </div>

            {hardFail && (
              <div className="banner crit" style={{ marginBottom: 16 }}>
                <Info />
                <span>Couldn't send that just now. Please retry in a moment.</span>
              </div>
            )}

            <button
              className="btn primary"
              type="submit"
              style={{ width: "100%", justifyContent: "center", marginTop: 4 }}
              disabled={!email.includes("@") || forgot.isPending}
            >
              {forgot.isPending ? "Sending…" : "Send reset link"}
            </button>

            <p className="auth-hint" style={{ marginTop: 14 }}>
              Remembered it? <Link to="/login">Back to sign in</Link>
            </p>
          </form>
        )}
      </div>
    </div>
  );
}
